<template tag="테이블">
  <BaseTableConfig
    v-if="!header"
    :totalCnt="tableOptions.serverItemLength"
    :checked="tableInfo.checkBox"
    :checkedCnt="allSelectedItems?.length"
    v-model:rowPerPage="rowsPerPage"
  >
    <template #top-btn-area>
      <template v-for="btn in tableInfo.btnList" :key="btn.id">
        <template v-if="btn.addText">
          <span :class="btn.textClass" class="mr-2">
            {{ btn.addText }}
          </span>
        </template>
        <v-btn
          v-if="btn.type === 'top' && (btn.viewYn ?? true)"
          class="mr-2"
          :class="btn.className"
          @click="btn.action(btn.param || '')"
        >
          {{ btn.name }}
        </v-btn>
      </template>
    </template>
    <template #left-btn-area>
      <template v-for="btn in tableInfo.btnList" :key="btn.id">
        <v-btn
          v-if="btn.type === 'left' && (btn.viewYn ?? true)"
          class="ml-2"
          :class="btn.className"
          @click="btn.action(btn.param || '')"
        >
          {{ btn.name }}
        </v-btn>
      </template>
    </template>
    <template #right-btn-area>
      <template v-for="btn in tableInfo.btnList" :key="btn.id">
        <v-btn
          v-if="btn.type === 'right' && (btn.viewYn ?? true)"
          class="mr-2"
          :class="btn.className"
          @click="btn.action(btn.param || '')"
        >
          {{ btn.name }}
        </v-btn>
      </template>
    </template>
  </BaseTableConfig>

  <v-easy-table
    ref="dataTable"
    :body-row-class-name="getRowClass"
    :headers="headers"
    :items="dataList"
    :loading="tableOptions.loading"
    v-model:items-selected="selectedItems"
    :server-items-length="tableOptions.serverItemLength"
    v-model:server-options="tableOptions.serverOptions"
    hideRowsPerPage
    headerTextDirection="center"
    alternating
    :emptyMessage="emptyMessage ?? $text('table.emptyMessage')"
    borderCell
    buttons-pagination
  >
    <template v-for="head of headers" v-slot:[`item-${head.value}`]="item" :key="head.value">
      <div :class="[head.className, head.align ? `text-${head.align}` : '']">
        <template v-if="head.type === 'text' || !head.type">
          <p>{{ item[head.value] }}</p>
        </template>
        <template v-else-if="head.type === 'date'">
          <p>{{ vm.$StrUtil.transDate(item[head.value], head.date || "") || "-" }}</p>
        </template>
        <template v-else-if="head.type === 'title'">
          <a @click="(head.slot as ItemTitle).action?.(item)">
            {{
              (head.slot as ItemTitle).textFn?.(item) ||
              (head.slot as ItemTitle).text ||
              item[head.value]
            }}
          </a>
        </template>
        <template v-else-if="head.type === 'btn'">
          <template v-for="btn of (head.slot as ItemBtn).btnList" :key="btn.text">
            <v-btn
              size="small"
              :disabled="btn.disabledFn ? btn.disabledFn(item) : btn.disabled"
              @click="btn.action(item)"
            >
              {{ btn.textFn ? btn.textFn(item) : btn.text }}
            </v-btn>
          </template>
        </template>
        <template v-else-if="head.type === 'switch'">
          <Base.ToggleSwitch
            :model-value="item[head.value] === 'Y'"
            @update:model-value="
              (val: boolean) => {
                item[head.value] = val ? 'Y' : 'N';
                (head.slot as ItemToggle).action?.(item, val);
              }
            "
          ></Base.ToggleSwitch>
        </template>
        <template v-else-if="head.type === 'dynamic'">
          <template v-if="(head.slot as ItemDynamic).dynamicTypeFn?.(item) === 'btn'">
            <template v-for="(btn, _index) in (head.slot as ItemDynamic)?.btnList" :key="_index">
              <Base.Button size="small" :disabled="btn.disabled" @click="() => btn.action?.(item)">
                {{
                  typeof btn.textFn === "function"
                    ? btn.textFn(item)
                    : btn.text
                      ? btn.text
                      : item[head.value]
                }}
              </Base.Button>
            </template>
          </template>
          <template v-else>
            <span
              :class="
                (head.slot as ItemDynamic).classNameFn?.(item) ||
                (head.slot as ItemDynamic).className
              "
            >
              {{
                (head.slot as ItemTitle).textFn?.(item) ||
                (head.slot as ItemTitle).text ||
                item[head.value]
              }}
            </span>
          </template>
        </template>
        <template v-else-if="head.type === 'moveBtn'">
          <span v-if="dataList.length === 1"> - </span>
          <Base.Button
            v-if="getDataIndex(item) > 0"
            size="small"
            @click="moveRowUp(getDataIndex(item), head.value)"
          >
            ▲
          </Base.Button>
          <Base.Button
            v-if="getDataIndex(item) !== dataList.length - 1"
            size="small"
            @click="moveRowDown(getDataIndex(item), head.value)"
          >
            ▼
          </Base.Button>
        </template>
        <template v-else-if="head.type === 'tooltip'">
          {{
            (head.slot as ItemTooltip).textFn?.(item) ||
            (head.slot as ItemTooltip).text ||
            item[head.value]
          }}
          <v-avatar class="mx-2" size="20" color="primary">
            <v-icon style="font-size: 14px">mdi-exclamation-thick</v-icon>
            <v-tooltip class="text-enter">
              {{ (head.slot as ItemTooltip).tooltip?.(item) }}
            </v-tooltip>
          </v-avatar>
        </template>
        <template v-else-if="head.type === 'radio'">
          <div>
            <Base.Radio
              :name="head.value"
              :options="[{ label: item.label || '', value: item[head.value] || item }]"
              v-model="radioData[head.value]"
            />
          </div>
        </template>
      </div>
    </template>
  </v-easy-table>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import BaseTableConfig from "@/common/base-components/admin/BaseTableConfig.vue";
import { global, i18n } from "@/common/plugins/global";
import type {
  ItemBtn,
  ItemDynamic,
  ItemTitle,
  ItemToggle,
  ItemTooltip,
  TableHeader,
  TableInfo,
} from "@/common/types/TableInfoTypes";
import { Base } from "@/common/base-components/BaseFactory.ts";

/* ------------------------------------------------------------
                          Vue Instance & Global Properties
   ------------------------------------------------------------ */
const { $text } = i18n();
const vm = global();

/* ------------------------------------------------------------
                          Props
   ------------------------------------------------------------ */
const props = defineProps({
  searchParam: {
    type: Object,
    required: true,
  },
  headers: {
    type: Array<TableHeader>,
    required: true,
  },
  tableInfo: {
    type: Object as () => TableInfo,
    required: true,
  },
  api: {
    type: Function,
    required: true,
  },
  emptyMessage: {
    type: String,
    default: () => "",
  },
});

/* ------------------------------------------------------------
                          Variable Declarations
   ------------------------------------------------------------ */
const dataTable = ref();
const radioData = ref<Record<string, string>>({});
const selectedItems = ref(props.tableInfo.checkBox ? [] : null);
const allSelectedItems = ref(props.tableInfo.checkBox ? [] : null);
const tableOptions = ref({
  serverOptions: {
    page: 1,
    rowsPerPage: props.tableInfo.rowsPerPage || 10,
    sortBy: props.tableInfo?.sortBy || [""],
    sortType: props.tableInfo?.sortType || [""],
  },
  serverItemLength: 0,
  loading: false,
});

/** Store previous search conditions to detect changes */
/** 변경 감지를 위한 이전 검색 조건 저장 */
const prevSearch = ref<string>("");
const header = ref(false);
/** Reactive rowsPerPage value for syncing with table component */
/** 테이블 컴포넌트와 동기화를 위한 반응형 rowsPerPage 값 */
const rowsPerPage = ref<number>(props.tableInfo.rowsPerPage || 10);
/**  Table data list */
/** 테이블 데이터 목록 */
const dataList = ref([]);
/** 하이라이트된 아이템 ID */
const highlightedId = ref<string | number | null>(null);

/* ------------------------------------------------------------
                          Methods
   ------------------------------------------------------------ */
/** Fetch data from API based on table options and search conditions */
/** 테이블 옵션과 검색 조건을 기반으로 API에서 데이터 가져오기 */
const searchData = async (pageNo?: number) => {
  if (props.tableInfo.checkBox && allSelectedItems.value && allSelectedItems.value.length > 0) {
    allSelectedItems.value = [];
  }
  getSearchData(pageNo);
};

/** 검색 데이터 가져오기 */
const getSearchData = async (pageNo?: number) => {
  radioData.value = {};
  highlightedId.value = null;

  if (pageNo && pageNo !== tableOptions.value.serverOptions.page) {
    tableOptions.value.serverOptions.page = pageNo;
    return;
  }

  const searchParam = {
    ...props.searchParam,
    ...vm.$serial(tableOptions.value.serverOptions),
  };
  const currentSearch = JSON.stringify({
    sortBy: tableOptions.value.serverOptions.sortBy,
    sortType: tableOptions.value.serverOptions.sortType,
  });

  if (prevSearch.value && prevSearch.value !== currentSearch) {
    if (props.tableInfo.checkBox && allSelectedItems.value!.length > 0) {
      allSelectedItems.value = [];
    }
  }
  prevSearch.value = currentSearch;

  tableOptions.value.loading = true;
  try {
    const listData = await props.api(searchParam);
    if (listData) {
      dataList.value = listData;
      tableOptions.value.serverItemLength = listData && listData.length > 0 ? listData[0].totalCount : 0;

      if (allSelectedItems.value) {
        selectedItems.value = dataList.value.filter((item) =>
          allSelectedItems.value?.some(
            (selected) => selected[props.tableInfo.keyId] === item[props.tableInfo.keyId]
          )
        );
      }
    }
  } finally {
    tableOptions.value.loading = false;
  }
};

/** 테이블 데이터 가져오기 */
const getTableData = () => dataList.value;
/** 테이블 데이터 설정 */
const setTableData = (setList: []) => {
  header.value = true;
  dataList.value = [...setList];
};
/** 라디오 데이터 가져오기 */
const getRadioData = () => radioData.value;
/** 데이터 인덱스 가져오기 */
const getDataIndex = (item: { no: number }) => {
  return dataList.value.findIndex((find) => find["no"] === item.no);
};
/** 현재 페이지 번호 가져오기 */
const getCurrPageNo = () => {
  return tableOptions.value.serverOptions.page;
};
/** 정렬 기준 가져오기 */
const getSortBy = () => {
  const { sortBy, sortType } = vm.$serial(tableOptions.value.serverOptions);
  return [sortBy, sortType];
};
/** 선택된 아이템 가져오기 */
const getSelectedItems = () => {
  if (allSelectedItems.value!.length === 0) {
    vm.$alert($text("alert.selectRowError"));
    throw new Error($text("alert.selectRowError"));
  }
  return allSelectedItems.value;
};
/** 위로 이동 */
const moveRowUp = (index: number, value: string) => {
  if (index > 0) {
    const items = [...dataList.value] as { no: number }[];
    [items[index - 1], items[index]] = [items[index], items[index - 1]];
    [items[index - 1].no, items[index].no] = [items[index].no, items[index - 1].no];
    dataList.value = items as [];
    triggerHighlight(items[index - 1].no);
    resetOrder(value);
    // head에 action이 있으면 실행
    const head = props.headers.find((h) => h.value === value);
    if (head?.slot && typeof (head.slot as { action?: unknown }).action === "function") {
      (head.slot as { action: (dataList: unknown[]) => void | Promise<void> }).action(
        dataList.value
      );
    }
  }
};
/** 아래로 이동 */
const moveRowDown = (index: number, value: string) => {
  if (index < dataList.value.length - 1) {
    const items = [...dataList.value] as { no: number }[];
    [items[index], items[index + 1]] = [items[index + 1], items[index]];
    [items[index].no, items[index + 1].no] = [items[index + 1].no, items[index].no];
    dataList.value = items as [];
    triggerHighlight(items[index + 1].no);
    resetOrder(value);
    // head에 action이 있으면 실행
    const head = props.headers.find((h) => h.value === value);
    if (head?.slot && typeof (head.slot as { action?: unknown }).action === "function") {
      (head.slot as { action: (dataList: unknown[]) => void | Promise<void> }).action(
        dataList.value
      );
    }
  }
};
/** 정렬 초기화 */
const resetOrder = (value: string) => {
  for (let i = 0; i < dataList.value.length; i++) {
    const data = dataList.value[i] as Record<string, number>;
    data[value] = i + 1;
  }
};

/** 하이라이트 트리거 */
function triggerHighlight(itemId: string | number) {
  highlightedId.value = null; // 재적용 가능하게 리셋
  requestAnimationFrame(() => {
    highlightedId.value = itemId;
  });
}
/** 행 클래스 가져오기 */
const getRowClass = (item: { no: number }) => {
  return item.no === highlightedId.value ? "highlight-row" : "";
};

/* ------------------------------------------------------------
                          watch
   ------------------------------------------------------------ */
/** 테이블 옵션이 변경될 때 자동으로 데이터 가져오기 */
watch(
  () => tableOptions.value.serverOptions,
  () => {
    getSearchData();
  },
  { deep: true }
);

/** Sync updated rowsPerPage with the table component */
/** 업데이트된 rowsPerPage를 테이블 컴포넌트와 동기화 */
watch(
  () => rowsPerPage.value,
  () => {
    dataTable.value.updateRowsPerPageActiveOption(rowsPerPage.value);
  }
);

/** 선택된 아이템이 변경될 때 전체 선택 목록 업데이트 */
watch(
  () => selectedItems.value,
  () => {
    if (props.tableInfo.checkBox && selectedItems.value && Array.isArray(selectedItems.value)) {
      selectedItems.value.forEach((item) => {
        if (
          !allSelectedItems.value?.some(
            (selected) => selected[props.tableInfo.keyId] === item[props.tableInfo.keyId]
          )
        ) {
          allSelectedItems.value?.push(item);
        }
      });

      allSelectedItems.value = allSelectedItems.value!.filter(
        (item) =>
          (selectedItems.value &&
            selectedItems.value.some(
              (selected) => selected[props.tableInfo.keyId] === item[props.tableInfo.keyId]
            )) ||
          !dataList.value.some(
            (dataItem) => dataItem[props.tableInfo.keyId] === item[props.tableInfo.keyId]
          )
      );
    }
  },
  { deep: true }
);

/* ------------------------------------------------------------
                          Expose
   ------------------------------------------------------------ */
defineExpose({
  searchData,
  getTableData,
  setTableData,
  getRadioData,
  getCurrPageNo,
  getSortBy,
  getSelectedItems,
});
</script>
